import { auth } from "../firebase/firebase";
import { Dispatch } from "@reduxjs/toolkit";
import { setUser, logout } from "../store/slices/session/sessionSlice";
import * as Sentry from "@sentry/react";

// Define user information type for Redux store updates
type UserInfo = {
  uid: string;
  email: string;
  token: string;
  creationTime: string;
  lastSignInTime: string;
};

// Track token refresh attempts to prevent infinite loops
let isRefreshing = false;
let refreshPromise: Promise<string> | null = null;

/**
 * Centralized token management service
 * Handles both proactive and reactive token refreshing
 */
export class TokenService {
  /**
   * Force refresh the Firebase token
   * @returns Promise with the new token
   */
  static async refreshToken(): Promise<string> {
    const user = auth.currentUser;
    if (!user) {
      throw new Error("No user is logged in");
    }
    // Force refresh the token
    return user.getIdToken(true);
  }

  /**
   * Update Redux store with user info and token
   * @param dispatch Redux dispatch function
   * @param user Firebase user object
   * @param token Firebase ID token
   */
  static async updateUserInStore(
    dispatch: Dispatch,
    token?: string
  ): Promise<void> {
    const user = auth.currentUser;
    if (!user) {
      throw new Error("No user is logged in");
    }

    const idToken = token || (await user.getIdToken());

    // Create user info object with the UserInfo type
    const userInfo: UserInfo = {
      uid: user.uid,
      email: user.email || "",
      token: idToken,
      creationTime: user.metadata.creationTime || "",
      lastSignInTime: user.metadata.lastSignInTime || "",
    };

    dispatch(setUser(userInfo));
  }

  /**
   * Handle reactive token refresh when API calls fail due to auth errors
   * @param dispatch Redux dispatch function
   * @param currentUser Current user from Redux store
   * @returns Promise with the new token
   */
  static async handleTokenRefresh(dispatch: Dispatch): Promise<string | null> {
    // If we're not already refreshing the token
    if (!isRefreshing) {
      console.log("Starting token refresh process...");
      isRefreshing = true;
      try {
        // Check if we're actually logged in first
        const firebaseUser = auth.currentUser;
        if (!firebaseUser) {
          console.error("No Firebase user found when trying to refresh token");
          console.log("No user to refresh token for, not logging out");
          return null; // Return null but don't throw an error or logout
        }

        // Create a single refresh promise that all concurrent requests can use
        console.log(`Refreshing token for user: ${firebaseUser.email}`);
        refreshPromise = this.refreshToken();
        const newToken = await refreshPromise;
        console.log(
          `Token refreshed successfully. New token length: ${newToken.length}`
        );

        // Update the token in Redux store
        await this.updateUserInStore(dispatch, newToken);
        console.log("Token refreshed reactively and updated in Redux");

        return newToken;
      } catch (error) {
        Sentry.captureException(error);
        console.error("Error refreshing token:", error);
        // Only log out if there was a Firebase user and token refresh genuinely failed
        if (auth.currentUser) {
          console.log("Token refresh failed for logged in user, logging out");
          dispatch(logout());
        } else {
          console.log(
            "Token refresh failed but no user found, not logging out"
          );
        }
        return null;
      } finally {
        isRefreshing = false;
        refreshPromise = null;
      }
    } else if (refreshPromise) {
      // If another request is already refreshing, wait for that to complete
      try {
        const newToken = await refreshPromise;
        return newToken;
      } catch (err) {
        Sentry.captureException(err);
        console.error(err);
        return null;
      }
    }
    return null;
  }

  /**
   * Decode JWT token and extract expiration time
   * @param token JWT token
   * @returns Expiration time in milliseconds or null if parsing fails
   */
  static getTokenExpirationTime(token: string): number | null {
    try {
      // Get token parts - Firebase tokens are properly formatted JWTs
      const tokenParts = token.split(".");

      // Check if the token is properly formatted
      if (tokenParts.length !== 3) {
        console.error("Invalid token format - expected JWT with 3 parts");
        return null;
      }

      // Decode the payload part (second part)
      const payloadBase64 = tokenParts[1];

      // Add padding to base64 string if needed
      const base64 = payloadBase64.replace(/-/g, "+").replace(/_/g, "/");
      const pad = base64.length % 4;
      const paddedBase64 = pad ? base64 + "=".repeat(4 - pad) : base64;

      let payload;
      try {
        // For web environment - use atob
        const decodedString = atob(paddedBase64);
        payload = JSON.parse(decodedString);
      } catch (atobError) {
        Sentry.captureException(atobError);
        console.warn(
          "atob decoding failed, falling back to alternative method",
          atobError
        );
        // Fallback method for older browsers or edge cases
        payload = JSON.parse(
          decodeURIComponent(escape(window.atob(paddedBase64)))
        );
      }

      // Validate the payload and extract expiration time
      if (!payload || typeof payload.exp !== "number") {
        console.error("Token payload missing expiration time:", payload);
        return null;
      }

      // Extract expiration time
      return payload.exp * 1000; // Convert to milliseconds
    } catch (error) {
      Sentry.captureException(error);
      console.error("Error decoding token:", error);
      return null;
    }
  }

  /**
   * Calculate time until token refresh is needed
   * @param token JWT token
   * @returns Time in milliseconds until refresh or fallback value
   */
  static getTimeUntilRefresh(token: string): number {
    const FALLBACK_REFRESH_INTERVAL = 30 * 60 * 1000; // 30 minutes

    const expirationTime = this.getTokenExpirationTime(token);
    if (!expirationTime) {
      return FALLBACK_REFRESH_INTERVAL;
    }

    const currentTime = Date.now();

    // Validate that expiration is in the future
    if (expirationTime <= currentTime) {
      console.warn("Token has already expired");
      return 0;
    }

    // Calculate refresh time: 5 minutes before token expiration
    return Math.max(0, expirationTime - currentTime - 5 * 60 * 1000);
  }
}
